/**
 * Toast Notification Provider
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { Toast } from '@/components/common/Toast'
import type { Toast as ToastType, ToastType as ToastVariant, ToastContextType } from '@/types/app.types'
import { generateId } from '@/lib/utils'

const ToastContext = createContext<ToastContextType | null>(null)

const DEFAULT_DURATION = 4000

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastType[]>([])

  const showToast = useCallback(
    (message: string, type: ToastVariant = 'info', duration: number = DEFAULT_DURATION) => {
      const id = generateId()
      const toast: ToastType = { id, message, type, duration }

      setToasts(prev => [...prev, toast])

      // Auto-remove toast after duration
      if (duration > 0) {
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id))
        }, duration)
      }
    },
    []
  )

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const value: ToastContextType = {
    toasts,
    showToast,
    hideToast,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// Custom hook to use toast context
export function useToast(): ToastContextType {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
